import { EventEmitter } from '@angular/core'
import { CreditTransactionType } from './credit-layer.model'
import { CreditTriggers, NetLossThresholdData } from './credit-trigger.model'

export interface CreditStructureEntity {
  creditStructure?: CreditStructure
  creditCalculationStructure?: CreditCalculationStructure
}

/** Used to bridge our SAGE db with `locktonre-sage-crd-api` */
export interface CreditStructure {
  id?: number
  program_id: number
  label: string
  description: string
  credit_calculation_structure_id: string
  checked?: boolean
  imageSrc?: string
}

/** Used to bridge our SAGE db with the GSE-specific SQL Server structure row */
export interface GseStructure {
  id?: number
  program_id: number
  label: string
  description: string
  gse_calculation_structure_id: string
  checked?: boolean
  imageSrc?: string
}

/** Contains one to many `CreditTransaction` layers */
export interface CreditCalculationStructure {
  _id?: string
  name: string
  metadata: Record<string, string>
  portfolio_id: string
  transactions: CreditTransaction[]
  modified_date: string
  selected_scenario: string
  selected_default_scenario: string
  selected_return_period?: string
  selected_default_return_period?: string
  collateral_percent?: number
  rorac_scenario?: string
}

export interface CreditAdminCalculationStructure {
  id: string
  name: string
  description: string
  program: string
  year: number
  carrier: string
  created: string
  updated: string
  creator: string
  lastUpdater: string
  creditCalculationStructureId: string
}

export interface DetachmentScheduleStep {
  start_month: number
  detach_pct_target: number
  dq_multiplier: number
}

export interface ReferenceTrancheTerms {
  min_ce_target: number
  delinquency_threshold_pct: number
  distressed_buckets: '60plus' | '90plus'
  delinquency_window_months: number
  cumulative_net_loss_schedule?: NetLossThresholdData
  cnl_gates_subordinate: boolean
  cnl_non_curable: boolean
  supplemental_target_pct?: number
  a1_cnl_threshold?: number
  a1_cnl_non_curable: boolean
  first_payment_date?: string
  premium_basis: 'prior_period' | 'after_stepdown'
}

/** Also known as a `Layer` - contains one to many `Credit Tranche` sub layers */
export interface CreditTransaction {
  _id: string // optional for database, but required for tower UI
  name: string
  metadata: Record<string, string>
  transaction_type: CreditTransactionType
  ceded_loss_scenarios: CreditCededLossScenario[]
  effective_date: string
  maturity_date: string
  call_date: string
  acquisition_date?: string
  call_type_selector: CallTypeSelector
  saved_pricing_curve_id?: number
  limit_stepdown_type: string
  triggers: CreditTriggers
  clean_up_call?: number
  detachment_schedule?: DetachmentScheduleStep[]
  reference_tranche?: ReferenceTrancheTerms
  ercf_capital_basis?: 'with_cca' | 'without_cca'
  apply_triggers_to_detach_schedule?: boolean
  retention_type?: 'fixed' | 'floating'
  lockout_period: string
  tranches: CreditTranche[]
  annual_lr_cap?: number
  aggregate_lr_cap?: number
  profit_commission?: number
  ceding_commission?: number
  margin?: number
  wacl?: number
  wahc?: number
  modified_date: string
  cession: number
  payment_frequency: CreditPaymentFrequency
  fixed_cost: number
  portfolio_id: string
}

export const TRANSACTION_TEMP_PREFIX = 'TRANSACTION'

/** A sub layer of a `Credit Transaction` */
export interface CreditTranche {
  _id?: string
  name: string
  metadata: Record<string, string>
  cession: number
  attach: number
  detach: number
  rol: number
  wacl: number
  wahc: number
  aggregate_limit: number
  ceded_loss_scenarios: CreditCededLossScenario[]
  pricing_curve_toggle: boolean
  modified_date: string
  // optimization properties
  min_cession?: number
  max_cession?: number
  cession_increment?: number
  min_attach?: number
  max_attach?: number
  attach_increment?: number
  min_detach?: number
  max_detach?: number
  detach_increment?: number
  // submission properties
  reinsurer_participation_percent?: number
  // GSE reference-tranche properties
  kind?: 'mezzanine' | 'a1_scheduled' | 'a1_pro_rata'
  a1_schedule?: number[]
  a1_additional_reduction_start_month?: number
}

export type CreditTrancheWithCategory = CreditTranche & { category: string }

interface CreditCededLossScenario {
  _id: string
  name: string
  metadata: Record<string, string>
  scenario: string
  model: string
  gross_loss_scenario_id: string
  ceded_loss_scenario_data: string
  return_period: string
  modified_date: string
}

export interface CreditStructureOptimizationInput {
  programId: number
  structure: CreditCalculationStructure
}

export enum CreditPaymentFrequency {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
}

export type CallTypeSelector = 'economic' | 'earliest' | 'never'

export const redrawGraph = new EventEmitter<void>()

const TEMPLATE_CESSION = 0.25
const TEMPLATE_ATTACH_XL = 0.025
const TEMPLATE_DETACH_XL = 0.05
const TEMPLATE_ATTACH_QS = 0
const TEMPLATE_DETACH_QS = 1
const TEMPLATE_ATTACH_ILN = 0.025
const TEMPLATE_DETACH_ILN = 0.05

const TRANCHE_BASE_XL: Partial<CreditTranche> = {
  cession: TEMPLATE_CESSION,
  attach: TEMPLATE_ATTACH_XL,
  detach: TEMPLATE_DETACH_XL,
}
const TRANCHE_BASE_QS: Partial<CreditTranche> = {
  cession: TEMPLATE_CESSION,
  attach: TEMPLATE_ATTACH_QS,
  detach: TEMPLATE_DETACH_QS,
}
const TRANCHE_BASE_ILN: Partial<CreditTranche> = {
  cession: TEMPLATE_CESSION,
  attach: TEMPLATE_ATTACH_ILN,
  detach: TEMPLATE_DETACH_ILN,
}
export const TRANCHE_BASE_MAPPING: Record<CreditTransactionType, Partial<CreditTranche>> = {
  pmi_agg_xl: TRANCHE_BASE_XL,
  pmi_qs: TRANCHE_BASE_QS,
  pmi_iln: TRANCHE_BASE_ILN,
  gse_agg_xl: TRANCHE_BASE_XL,
  cm_xol: TRANCHE_BASE_ILN,
}

export enum CreditTrancheDefaultColor {
  pmi_agg_xl = '#00aeef',
  pmi_qs = '#f68a33',
  pmi_iln = '#01c96d',
  gse_agg_xl = '#00aeef',
  cm_xol = '#01c96d',
}

export const TRANSACTION_BASE_XOL_AND_ILN: Partial<CreditTransaction> = {
  clean_up_call: 0.1,
}

export const TRANSACTION_BASE_QS: Partial<CreditTransaction> = {
  cession: TEMPLATE_CESSION,
  annual_lr_cap: 3,
  aggregate_lr_cap: 2,
  profit_commission: 0.7,
  ceding_commission: 0.3,
}

export const NEW_STRUCTURE_LABEL = 'New Structure'
export const NEW_TRANSACTION_LABEL = 'New Transaction'
export const NEW_TRANCHE_LABEL = 'New Tranche'
export const NEW_OPTIMIZATION_STRUCTURE_DESCRIPTION = 'Built with Optimizer'
